import { Link } from "react-router-dom";
import {
  FaFacebookF,
  FaLinkedinIn,
  FaInstagram,
  FaXTwitter,
} from "react-icons/fa6";
import { ROUTES, SERVICE_ROUTES } from "../../../constants/routes";
import logo from "../../../assets/images/logo.png";
import iso9001Logo from "../../../assets/images/certifications/iso-9001.png";
import iso14001Logo from "../../../assets/images/certifications/iso-14001.png";
import iso45001Logo from "../../../assets/images/certifications/iso-45001.png";
import iadcMemberWhiteLogo from "../../../assets/images/certifications/iadc-member-white.png";

const CERTIFICATION_LOGOS = [
  { src: iso9001Logo, alt: "ISO 9001 Quality Management System" },
  { src: iso14001Logo, alt: "ISO 14001 Environmental Management System" },
  { src: iso45001Logo, alt: "ISO 45001 Health and Safety Certified" },
  {
    src: iadcMemberWhiteLogo,
    alt: "IADC Member",
    // Same visual height as ISO seals; wider box so landscape mark isn’t shrunk
    wide: true,
  },
];

const CERT_BOX =
  "flex shrink-0 items-center justify-center overflow-visible";
const CERT_BOX_ISO = `${CERT_BOX} h-14 w-14 sm:h-16 sm:w-16 md:h-[4.25rem] md:w-[4.25rem]`;
const CERT_BOX_IADC = `${CERT_BOX} h-14 w-[7.25rem] sm:h-16 sm:w-[8.25rem] md:h-[4.25rem] md:w-[8.75rem]`;
const CERT_IMG = "h-full w-full object-contain object-center";

function Footer() {
  const currentYear = new Date().getFullYear();

  const primaryQuickLinks = [
    { name: "Home", path: ROUTES.HOME },
    { name: "About Us", path: ROUTES.ABOUT },
    { name: "Services", path: ROUTES.SERVICES },
    { name: "Projects", path: ROUTES.PROJECTS },
    { name: "Clients", path: ROUTES.CLIENTS },
    { name: "Careers", path: ROUTES.CAREERS },
    { name: "Contact", path: ROUTES.CONTACT },
  ];

  const additionalQuickLinks = [
    { name: "HSEQ", path: ROUTES.HSEQ },
    {
      name: "Global Workforce Solutions & Crew Management (GWS)",
      path: ROUTES.GWS,
    },
    { name: "Awards and Recognitions", path: ROUTES.AWARDS_AND_RECOGNITIONS },
    { name: "Gallery", path: ROUTES.GALLERY },
    { name: "Major Milestones", path: "/major-milestone" },
  ];

  const services = [
    { name: "Operations & Maintenance", path: SERVICE_ROUTES.om },
    { name: "Manpower Supply", path: SERVICE_ROUTES.manpower },
    { name: "Drilling Services", path: SERVICE_ROUTES.drilling },
    { name: "Workover & Well Intervention Services", path: SERVICE_ROUTES.workover },
  ];

  const offices = [
    {
      title: "Ahmedabad (Registered Office)",
      address:
        "402, Orange Mall, Mehsana Highway, Above HDFC Bank, Nigam Nagar, Chandkheda, Ahmedabad, Gujarat 382424",
      phone: "+91 9316305258",
      phoneHref: "tel:+919316305258",
    },
    {
      title: "Mumbai (Corporate Office)",
      address:
        "1209, 12th Floor, Hubtown Viva Building, Near Mogra Metro Station, Jogeshwari East, Mumbai – 400060",
      email: "corporate@southasiaconsultancy.com",
    },
    {
      title: "Sharjah (U.A.E.)",
      address:
        "South Asia Consultancy FZE, Saif Plus R6 - 11/B, P.O. Box 120197, Sharjah, U.A.E.",
      phone: "+971 52 601 3353",
      phoneHref: "tel:+971526013353",
      email: "sharjah@southasiaconsultancy.com",
    },
    {
      title: "Dubai (U.A.E.)",
      address:
        "1604, 16th floor, Regal Tower, Al Mustaqbal street, Near business bay business bay, off. Shaikh Zayed road, Dubai - United Arab Emirates.",
      phone: "+971 52 601 3353",
      phoneHref: "tel:+971526013353",
      email: "dubai@southasiaconsultancy.com",
    },
    {
      title: "Operations Office",
      address:
        "619, Palladium Business Hub, Opposite 4D Mall, Chandkheda, Ahmedabad, Gujarat 382424",
      email: "contact@southasiaconsultancy.com",
    },
    {
      title: "France Office",
      address:
        "21, rue du Village d'Entreprise, 31670 Labege, Toulouse, France.",
      phone: "+91 9316305258",
      phoneHref: "tel:+919316305258",
      email: "info@southasiaconsultancy.com",
    },
  ];

  return (
    <footer className="bg-gradient-to-br from-[#071A2F] via-[#0B2341] to-[#102D52] text-white">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-12 lg:items-start lg:gap-6 xl:gap-8 lg:pl-2 xl:pl-4">
          <div className="lg:col-span-5">
            <div className="mb-6 flex flex-nowrap items-center gap-2.5 sm:gap-3 md:gap-4">
              <Link
                to={ROUTES.HOME}
                aria-label="South Asia Consultancy Home"
                className="inline-block shrink-0"
              >
                <img
                  src={logo}
                  alt="South Asia Consultancy"
                  className="h-20 w-auto object-contain transition-transform duration-300 hover:scale-105 md:h-24"
                  loading="eager"
                  decoding="async"
                />
              </Link>

              {CERTIFICATION_LOGOS.map((cert) => (
                <span
                  key={cert.alt}
                  className={cert.wide ? CERT_BOX_IADC : CERT_BOX_ISO}
                >
                  <img
                    src={cert.src}
                    alt={cert.alt}
                    className={CERT_IMG}
                    loading="lazy"
                    decoding="async"
                  />
                </span>
              ))}
            </div>
            <p className="text-slate-300 leading-relaxed text-sm text-justify">
              Integrated energy services partner delivering drilling, workover,
              maintenance, workforce, and oilfield project solutions across
              offshore and onshore operations.
            </p>

            <div className="flex gap-3 mt-6">
              {[
                {
                  href: "https://www.facebook.com/SouthAsiaConsultancy/",
                  Icon: FaFacebookF,
                  label: "Facebook",
                },
                {
                  href: "https://www.linkedin.com/company/southasiaconsultancy/",
                  Icon: FaLinkedinIn,
                  label: "LinkedIn",
                },
                {
                  href: "https://x.com/DSR_SOUTHASIA",
                  Icon: FaXTwitter,
                  label: "X (Twitter)",
                },
                {
                  href: "https://www.instagram.com/southasiaconsultancy/",
                  Icon: FaInstagram,
                  label: "Instagram",
                },
              ].map(({ href, Icon, label }) => (
                <a
                  key={href}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Follow SAC on ${label}`}
                  className="w-10 h-10 flex items-center justify-center border border-white/20 rounded-full hover:bg-red-600 hover:border-red-600 transition-all duration-300 hover:-translate-y-1"
                >
                  <Icon size={15} aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-4 lg:pl-4 xl:pl-6">
            <div className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
              <div className="col-start-1 row-start-1">
                <h4 className="text-xl font-semibold mb-4">Quick Links</h4>
                <div className="w-10 h-1 bg-red-600 mb-5" />
              </div>

              <ul className="col-start-1 row-start-2 space-y-2">
                {primaryQuickLinks.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.path}
                      className="text-slate-300 hover:text-red-500 transition-colors duration-300"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>

              <ul className="col-start-1 row-start-3 space-y-2 sm:col-start-2 sm:row-start-2">
                {additionalQuickLinks.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.path}
                      className="text-slate-300 hover:text-red-500 transition-colors duration-300 leading-relaxed"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="lg:col-span-3 lg:pl-2 xl:pl-4">
            <h4 className="text-xl font-semibold mb-4">Services</h4>
            <div className="w-10 h-1 bg-red-600 mb-5" />
            <ul className="space-y-2">
              {services.map((service) => (
                <li key={service.name}>
                  <Link
                    to={service.path}
                    className="text-slate-300 hover:text-red-500 transition-colors duration-300 leading-relaxed"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10 bg-[#081b33]">
        <div className="max-w-[1400px] mx-auto px-6 py-8">
          <div className="text-center mb-6">
            <h4 className="text-white text-lg font-semibold">Our Offices</h4>
            <div className="w-16 h-[2px] bg-red-600 mx-auto mt-2" />
          </div>

          <div className="grid grid-cols-1 gap-x-8 gap-y-8 sm:grid-cols-2 lg:grid-cols-3 text-sm items-start">
            {offices.map((office) => (
              <div key={office.title} className="w-full min-w-0">
                <h5 className="text-red-500 font-semibold mb-2">{office.title}</h5>
                <p className="text-slate-300 leading-relaxed">{office.address}</p>
                {office.phone && (
                  <a
                    href={office.phoneHref}
                    className="text-slate-400 mt-2 inline-block hover:text-white transition-colors"
                  >
                    {office.phone}
                  </a>
                )}
                {office.email && (
                  <a
                    href={`mailto:${office.email}`}
                    className="text-slate-400 mt-2 block hover:text-white transition-colors"
                  >
                    {office.email}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-red-700 via-red-600 to-red-700">
        <div className="max-w-[1400px] mx-auto px-6 py-4 space-y-2">
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-1 text-sm text-white/90">
            <Link to={ROUTES.PRIVACY_POLICY} className="hover:text-white hover:underline">
              Privacy Policy
            </Link>
            <Link to={ROUTES.TERMS_OF_USE} className="hover:text-white hover:underline">
              Terms of Use
            </Link>
            <Link to={ROUTES.COOKIE_POLICY} className="hover:text-white hover:underline">
              Cookie Policy
            </Link>
          </div>
          <p className="text-center text-sm text-white">
            © {currentYear} South Asia Consultancy Services Pvt. Ltd. All Rights
            Reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
